import {Platform, StyleSheet, View} from "react-native";
import {Color} from "@/lib/styles";
import {KeyboardEvent, useRef, useState} from "react";
import RNDateTimePicker, {DateTimePickerAndroid, DateTimePickerEvent} from "@react-native-community/datetimepicker";
import {parseDateTime, toIsoDate, toIsoTime} from "@/lib/time";
import ActionButton from "@/lib/components/ActionButton";
import Timestamp from "@/lib/components/Timestamp";

type Props = {
    onChange: (date: string, time: string) => void,
    onSubmit: () => any,
    // Timezone-aware RFC3339 string.
    // Any timezone information will be discarded.
    initialDate: string,
}

// Calendar that returns its date and time as `YYYY-mm-dd` and `HH:mm`, respectively.
export function Calendar({onChange, onSubmit, initialDate}: Props) {
    const initDate = parseDateTime(initialDate);
    const [nativeDate, setNativeDate] = useState<Date | undefined>(initDate);
    const [textDate, setTextDate] = useState<string>(initDate ? toIsoDate(initDate) : "");
    const [textTime, setTextTime] = useState<string>(initDate ? toIsoTime(initDate) : "");
    const dateRef = useRef<HTMLInputElement>(null);
    const timeRef = useRef<HTMLInputElement>(null);

    function setSelected(_event: DateTimePickerEvent, date?: Date) {
        setNativeDate(date);
        const txtDate = (date ? toIsoDate(date) : "");
        const txtTime = (date ? toIsoTime(date) : "");
        setTextDate(txtDate);
        setTextTime(txtTime);
        onChange(txtDate, txtTime);
    }

    function textDateChanged() {
        if (!dateRef || !dateRef.current) {
            return;
        }
        setTextDate(dateRef.current.value);
        onChange(dateRef.current.value, textTime);
    }

    function textTimeChanged() {
        if (!timeRef || !timeRef.current) {
            return;
        }
        setTextTime(timeRef.current.value);
        onChange(textDate, timeRef.current.value);
    }

    function enterSubmitsForm(event: KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Enter") {
            onSubmit();
        }
    }

    if (Platform.OS === "web") {
        return (
            <View style={styles.container}>
                <input
                    ref={dateRef}
                    type={"date"}
                    style={styles.textInput}
                    pattern={`\d{4}-\d{2}-\d{2}`}
                    value={textDate}
                    onKeyDown={enterSubmitsForm}
                    onChange={textDateChanged}
                />
                <input
                    ref={timeRef}
                    type={"time"}
                    style={styles.textInput}
                    pattern={`\d{2}:\d{2}`}
                    value={textTime}
                    onKeyDown={enterSubmitsForm}
                    onChange={textTimeChanged}
                />
            </View>
        );
    } else if (Platform.OS === "android") {
        const date = nativeDate || new Date(Date.now());
        // component must be mounted only when you want to use it
        return (
            <View style={styles.outer}>
                <View style={styles.container}>
                    <Timestamp start={date}/>
                </View>
                <View style={styles.container}>
                    <ActionButton
                        icon={"date"}
                        title={"Endre dato"}
                        onPress={() => {
                            DateTimePickerAndroid.open({
                                mode: "date",
                                value: date,
                                firstDayOfWeek: 1,
                                display: "spinner",
                                onChange: setSelected,
                            });
                        }}
                    />
                    <ActionButton
                        icon={"clock"}
                        title={"Endre tid"}
                        onPress={() => {
                            DateTimePickerAndroid.open({
                                mode: "time",
                                value: date,
                                display: "spinner",
                                onChange: setSelected,
                            });
                        }}
                    />
                </View>
            </View>
        );
    } else {
        // iOS
        const date = nativeDate || new Date(Date.now());
        return (
            <View style={styles.container}>
                <RNDateTimePicker value={date}
                                  mode={"date"}
                                  firstDayOfWeek={1}
                                  locale={"nb"}
                                  themeVariant={"dark"}
                                  onChange={setSelected}
                />
                <RNDateTimePicker value={date}
                                  mode={"time"}
                                  firstDayOfWeek={1}
                                  locale={"nb"}
                                  themeVariant={"dark"}
                                  onChange={setSelected}
                />
            </View>
        );
    }
}

export const styles = StyleSheet.create({
    outer: {
        gap: 8,
    },
    container: {
        flexDirection: "row",
        gap: 6,
        justifyContent: "center",
        alignContent: "center",
        alignItems: "center"
    },
    text: {
        color: Color.PrimaryText,
        fontSize: 16,
        lineHeight: 26,
        width: "100%",
    },
    textInput: {
        backgroundColor: Color.Background,
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: Color.Yellow,
        borderRadius: 7,
        padding: 8,
        fontSize: 16,
        color: Color.PrimaryText,
        marginBottom: 8,
        marginLeft: "auto",
        marginRight: "auto",
    },
});